import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  case_reference: string | null
}

interface DayEntry {
  id: number
  type: "T" | "E"
  emp_name: string | null
  service_item: string | null
  description: string | null
  notes: string | null
  amount: number
}

interface DayGroup {
  date: string
  day_name: string
  entries: DayEntry[]
  otj_minutes: number
  otj_total: number
  mileage: number
  mileage_total: number
  expense_total: number
  day_total: number
}

interface SummaryRow {
  date: string
  day_name: string
  otj_minutes: number
  otj_total: number
  mileage: number
  mileage_total: number
  expense_total: number
  day_total: number
}

interface GrandTotals {
  otj_minutes: number
  otj_total: number
  mileage: number
  mileage_total: number
  expense_total: number
  grand_total: number
}

interface Props {
  case_record: CaseRecord
  start_date: string | null
  end_date: string | null
  days: DayGroup[]
  summary: SummaryRow[]
  grand_totals: GrandTotals
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDollars(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

function formatTimeHM(minutes: number): string {
  if (minutes <= 0) return ""
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}h ${m}m`
}

/* ── Main Component ────────────────────────────────────── */

export default function SupportingDetail({
  case_record,
  start_date,
  end_date,
  days,
  summary,
  grand_totals,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    {
      title: "Supporting Detail",
      href: `/cases/${case_record.id}/billing_reports/supporting_detail`,
    },
  ]

  const [startDate, setStartDate] = useState(start_date ?? "")
  const [endDate, setEndDate] = useState(end_date ?? "")

  const basePath = `/cases/${case_record.id}/billing_reports/supporting_detail`

  const handleFilter = () => {
    const params = new URLSearchParams()
    if (startDate) params.set("start_date", startDate)
    if (endDate) params.set("end_date", endDate)
    const qs = params.toString()
    router.get(`${basePath}${qs ? `?${qs}` : ""}`)
  }

  const handleClearFilter = () => {
    setStartDate("")
    setEndDate("")
    router.get(basePath)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Supporting Detail - ${case_record.display_name}`} />

      <div className="p-6">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold">
            Supporting Detail — {case_record.display_name}
          </h1>
          {(start_date ?? end_date) && (
            <p className="text-hpi-med-gray mt-1 text-sm">
              {start_date && end_date
                ? `${start_date} through ${end_date}`
                : start_date
                  ? `From ${start_date}`
                  : `Through ${end_date}`}
            </p>
          )}
          {case_record.case_reference && (
            <p className="text-hpi-active-blue mt-1 text-sm">
              <strong>Client&apos;s Case/File#:</strong>{" "}
              {case_record.case_reference}
            </p>
          )}
        </div>

        {/* Date filter */}
        <div className="border-hpi-light-gray bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded border p-4">
          <div className="flex items-center gap-2">
            <label htmlFor="sd_start" className="text-hpi-med-gray text-sm">
              Start Date
            </label>
            <input
              id="sd_start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="sd_end" className="text-hpi-med-gray text-sm">
              End Date
            </label>
            <input
              id="sd_end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
            />
          </div>
          <button
            onClick={handleFilter}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1 text-sm text-white"
          >
            Filter
          </button>
          {(startDate || endDate) && (
            <button
              onClick={handleClearFilter}
              className="text-hpi-med-gray hover:text-hpi-dark-gray text-sm underline"
            >
              Clear
            </button>
          )}
        </div>

        {days.length === 0 ? (
          <div className="border-hpi-light-gray rounded border bg-white p-8 text-center">
            <p className="text-hpi-med-gray">
              No approved time entries or expenses found for this case.
            </p>
          </div>
        ) : (
          <>
            {/* Day-by-day sections */}
            {days.map((day) => (
              <div key={day.date} className="mb-6">
                <div className="bg-hpi-dark-blue rounded-t px-4 py-2.5">
                  <h3 className="text-sm font-semibold tracking-wider text-white">
                    {day.day_name}
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-hpi-pale-gray text-hpi-active-blue">
                        <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                          By
                        </th>
                        <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                          Service Item
                        </th>
                        <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                          Description
                        </th>
                        <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {day.entries.map((entry) => {
                        const entryHref =
                          entry.type === "T"
                            ? `/cases/${case_record.id}/time_entries/${entry.id}`
                            : `/cases/${case_record.id}/expenses/${entry.id}`

                        return (
                          <tr
                            key={`${entry.type}-${entry.id}`}
                            className="hover:bg-hpi-pale-blue bg-white"
                          >
                            <td className="border-hpi-light-gray border px-2 py-1.5">
                              <Link
                                href={entryHref}
                                className="text-hpi-active-blue hover:underline"
                              >
                                {entry.emp_name}
                              </Link>
                            </td>
                            <td className="border-hpi-light-gray border px-2 py-1.5">
                              <Link
                                href={entryHref}
                                className="text-hpi-active-blue hover:underline"
                              >
                                {entry.service_item}
                              </Link>
                            </td>
                            <td className="border-hpi-light-gray border px-2 py-1.5">
                              <Link
                                href={entryHref}
                                className="text-hpi-active-blue hover:underline"
                              >
                                {entry.description}
                                {entry.notes && (
                                  <div className="text-hpi-med-gray mt-0.5 text-[10px] italic">
                                    {entry.notes}
                                  </div>
                                )}
                              </Link>
                            </td>
                            <td className="border-hpi-light-gray border px-2 py-1.5 text-right whitespace-nowrap">
                              <Link
                                href={entryHref}
                                className="text-hpi-active-blue hover:underline"
                              >
                                {formatDollars(entry.amount)}
                              </Link>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Day subtotals */}
                <div className="border-hpi-light-gray grid grid-cols-4 gap-4 border border-t-0 bg-gray-50 px-4 py-2 text-xs">
                  {day.otj_minutes > 0 && (
                    <div>
                      <span className="text-hpi-med-gray">OTJ:</span>{" "}
                      <span className="font-medium">
                        {formatTimeHM(day.otj_minutes)} (
                        {formatDollars(day.otj_total)})
                      </span>
                    </div>
                  )}
                  {day.mileage > 0 && (
                    <div>
                      <span className="text-hpi-med-gray">Mileage:</span>{" "}
                      <span className="font-medium">
                        {day.mileage} ({formatDollars(day.mileage_total)})
                      </span>
                    </div>
                  )}
                  {day.expense_total > 0 && (
                    <div>
                      <span className="text-hpi-med-gray">Expenses:</span>{" "}
                      <span className="font-medium">
                        {formatDollars(day.expense_total)}
                      </span>
                    </div>
                  )}
                  <div className="text-right font-semibold">
                    Day Total: {formatDollars(day.day_total)}
                  </div>
                </div>
              </div>
            ))}

            {/* Summary table: subtotal by day */}
            <div className="mb-6">
              <div className="bg-hpi-dark-blue rounded-t px-4 py-2.5">
                <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
                  Subtotal by Day
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="bg-hpi-pale-gray text-hpi-active-blue">
                      <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                        Day
                      </th>
                      <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        Time
                      </th>
                      <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        Amount
                      </th>
                      <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        Miles
                      </th>
                      <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        Amount
                      </th>
                      <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        Expenses
                      </th>
                      <th className="border-hpi-light-gray border px-2 py-1.5 text-right font-semibold">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((row) => (
                      <tr
                        key={row.date}
                        className="hover:bg-hpi-pale-blue bg-white"
                      >
                        <td className="border-hpi-light-gray border px-2 py-1.5 whitespace-nowrap">
                          {row.day_name}
                        </td>
                        <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                          {formatTimeHM(row.otj_minutes)}
                        </td>
                        <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                          {row.otj_total > 0
                            ? formatDollars(row.otj_total)
                            : ""}
                        </td>
                        <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                          {row.mileage > 0 ? row.mileage : ""}
                        </td>
                        <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                          {row.mileage_total > 0
                            ? formatDollars(row.mileage_total)
                            : ""}
                        </td>
                        <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                          {row.expense_total > 0
                            ? formatDollars(row.expense_total)
                            : ""}
                        </td>
                        <td className="border-hpi-light-gray border px-2 py-1.5 text-right font-semibold">
                          {formatDollars(row.day_total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-hpi-pale-gray font-bold">
                      <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        Total
                      </td>
                      <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        {formatTimeHM(grand_totals.otj_minutes)}
                      </td>
                      <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        {grand_totals.otj_total > 0
                          ? formatDollars(grand_totals.otj_total)
                          : ""}
                      </td>
                      <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        {grand_totals.mileage > 0 ? grand_totals.mileage : ""}
                      </td>
                      <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        {grand_totals.mileage_total > 0
                          ? formatDollars(grand_totals.mileage_total)
                          : ""}
                      </td>
                      <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        {grand_totals.expense_total > 0
                          ? formatDollars(grand_totals.expense_total)
                          : ""}
                      </td>
                      <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                        {formatDollars(grand_totals.grand_total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
